import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { ProductPlaceholder } from "@/components/ProductPlaceholder";
import { formatPrice } from "@/lib/format";
import { GRADE_INFO } from "@/data/content";
import { useCart } from "@/context/CartContext";
import { cn } from "@/lib/utils";
import { primaryPhoto, CLD_CARD } from "@/lib/variants";
import { CloudImage } from "@/components/CloudImage";

export function ProductCard({ product, className = "" }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const variant = (product.variants || [])[0];
  const grade = (product.available_grades || [])[0];
  const cardPhoto = primaryPhoto(product);
  const conditionParts = [GRADE_INFO[grade]?.title || (product.condition === "nuovo" ? "Nuovo" : "Ricondizionato")];
  if (product.category_label || product.category) conditionParts.push(product.category_label || product.category);

  const BADGES = {
    offerta: { label: "Offerta", cls: "bg-lime text-ink" },
    ultimi_pezzi: { label: "Ultimi pezzi", cls: "bg-carrot text-ink" },
    novita: { label: "Novità", cls: "bg-[#2A2A30] text-foreground" },
  };
  const badge = BADGES[product.badge];
  const strike = product.on_sale ? product.strikethrough_price : null;

  const handleBuy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!variant) return;
    addItem({
      product_id: product.id,
      slug: product.slug,
      name: product.name,
      brand: product.brand,
      grade: variant.grade,
      unit_price: variant.price,
      image: cardPhoto?.url || null,
    });
    toast.success("Aggiunto al carrello", { description: `${product.name} · ${product.brand}` });
  };

  return (
    <Link
      to={`/prodotto/${product.slug}`}
      className={cn(
        "group flex h-full flex-col rounded-2xl bg-card p-4 transition-colors duration-300 ease-out hover:bg-surface3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime",
        className
      )}
      data-testid="product-card"
    >
      <div className="relative">
        {badge && (
          <span className={cn("absolute left-2 top-2 z-10 rounded-full px-3 py-1 text-xs font-semibold", badge.cls)} data-testid="product-card-badge">
            {badge.label}
          </span>
        )}
        <div className="aspect-square w-full overflow-hidden rounded-2xl bg-transparent">
          {cardPhoto ? (
            <CloudImage
              url={cardPhoto.url}
              transform={CLD_CARD}
              alt={cardPhoto.alt || product.name}
              loading="lazy"
              className="h-full w-full object-contain p-[10%] transition-transform duration-300 ease-out group-hover:scale-[1.03]"
              data-testid="product-card-image"
            />
          ) : (
            <ProductPlaceholder label={product.name} size="md" className="h-full w-full" />
          )}
        </div>
      </div>

      <h3 className="mt-4 font-display text-base font-bold leading-snug tracking-tight text-foreground line-clamp-2">
        {product.name}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground line-clamp-1">{conditionParts.join(" · ")}</p>

      {product.review_count > 0 && (
        <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground" data-testid="product-card-rating">
          <Star className="h-3.5 w-3.5 fill-lime text-lime" />
          <span className="font-medium text-foreground">{String(product.review_avg).replace(".", ",")}</span>
          <span>({product.review_count})</span>
        </div>
      )}

      <div className="mt-3">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-2xl font-extrabold tracking-tight text-foreground">
            {formatPrice(product.min_price)}
          </span>
          {strike && (
            <span className="text-sm text-muted-foreground line-through">{formatPrice(strike)}</span>
          )}
        </div>
        {product.new_price_reference && (
          <div className="mt-0.5 text-xs text-muted-foreground">Prezzo del nuovo {formatPrice(product.new_price_reference)}</div>
        )}
      </div>

      <button
        type="button"
        onClick={handleBuy}
        className="mt-4 h-11 w-full rounded-full bg-lime text-sm font-bold text-ink transition-transform duration-150 ease-out active:scale-[0.98]"
        data-testid="product-card-add-to-cart-button"
        aria-label={`Acquista ${product.name}`}
      >
        Acquista
      </button>
    </Link>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="flex h-full flex-col rounded-2xl bg-card p-4" data-testid="product-card-skeleton">
      <div className="aspect-square w-full animate-pulse rounded-2xl bg-surface3" />
      <div className="mt-4 h-4 w-3/4 animate-pulse rounded bg-surface3" />
      <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-surface3" />
      <div className="mt-4 h-6 w-1/3 animate-pulse rounded bg-surface3" />
      <div className="mt-4 h-11 w-full animate-pulse rounded-full bg-surface3" />
    </div>
  );
}
